import { escapeLayerSelector, registerModal, setModalMountNode } from "@carbon6/c6-frontend-design";
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
} from "react";
import { isHotkeySurfaceActive, useWindowHotkeys, type WindowHotkeyBinding } from "../keyboard";

type ModalSurfaceOptions = {
  active: boolean;
  hotkeys?: readonly WindowHotkeyBinding[];
  onBackdrop?: () => void;
  onRequestClose: () => void;
  rootAttributeName: `data-${string}`;
  zIndex: number;
};

type ModalSurface = {
  container: HTMLDivElement | null;
  dialogRef: RefObject<HTMLDivElement | null>;
  onBackdropClick: (event: ReactMouseEvent<HTMLDivElement>) => void;
  onBackdropMouseDown: (event: ReactMouseEvent<HTMLDivElement>) => void;
};

// c6 Modal이 아닌 body portal surface가 modalStack과 같은 생명주기·Escape 양보 규칙을 쓰게 한다.
export function useModalSurface({
  active,
  hotkeys = [],
  onBackdrop,
  onRequestClose,
  rootAttributeName,
  zIndex,
}: ModalSurfaceOptions): ModalSurface {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const backdropPointerDownRef = useRef(false);
  const onRequestCloseRef = useRef(onRequestClose);
  onRequestCloseRef.current = onRequestClose;

  // 최소화는 본문을 보존하므로 portal 컨테이너는 컴포넌트 수명 동안 유지한다.
  useEffect(() => {
    const root = document.createElement("div");
    root.setAttribute(rootAttributeName, "");
    document.body.appendChild(root);
    setContainer(root);
    return () => {
      setContainer(null);
      root.remove();
    };
  }, [rootAttributeName]);

  // 모달 등록·스크롤 잠금·포커스는 창이 보이는 구간에만 적용한다.
  useEffect(() => {
    if (!active || container === null) {
      return;
    }
    const restoreFocusTo =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unregister = registerModal(container, zIndex, restoreFocusTo);
    setModalMountNode(container, dialogRef.current);
    dialogRef.current?.focus();
    return unregister;
  }, [active, container, zIndex]);

  const handleEscape = (): void => {
    // 위 modal이 이 surface를 inert 처리했거나 내부 dismissable layer가 Escape를 먼저 쓰면 양보한다.
    if (!isHotkeySurfaceActive(dialogRef.current)) {
      return;
    }
    if (container?.querySelector(escapeLayerSelector("escape")) != null) {
      return;
    }
    onRequestCloseRef.current();
  };
  useWindowHotkeys({
    surfaceRef: dialogRef,
    bindings: [{ key: "Escape", guard: "raw", onPress: handleEscape }, ...hotkeys],
  });

  return {
    container,
    dialogRef,
    onBackdropClick: (event) => {
      if (event.target === event.currentTarget && backdropPointerDownRef.current) {
        onBackdrop?.();
      }
    },
    onBackdropMouseDown: (event) => {
      backdropPointerDownRef.current = event.target === event.currentTarget;
    },
  };
}
